import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '@inmar/component-lib';

// ============================================================================
// Types
// ============================================================================

export interface CsvErrorModalClosedEvent {
  element: CsvErrorModal;
}

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementEventMap {
    'error-modal-closed': CustomEvent<CsvErrorModalClosedEvent>;
  }
  interface HTMLElementTagNameMap {
    'csv-error-modal': CsvErrorModal;
  }
}

/**
 * Error modal for the csv-importer component.
 *
 * Displays an error alert banner inside an `<inmar-hc-modal>` with a Close button.
 *
 * @fires error-modal-closed - Emitted when the modal is closed (via Close button or X button).
 *   Detail: `{ element: CsvErrorModal }`
 */
@customElement('csv-error-modal')
export class CsvErrorModal extends LitElement {
  // ============================================================================
  // Properties
  // ============================================================================

  /** Whether the error modal is visible. */
  @property({ type: Boolean, reflect: true })
  open = false;

  /** Title displayed in the modal header. */
  @property({ type: String, attribute: 'modal-title' })
  modalTitle = 'Import';

  /** Heading text displayed in the alert banner. */
  @property({ type: String, attribute: 'heading-text' })
  headingText = '';

  /** Body text displayed in the alert banner. */
  @property({ type: String, attribute: 'body-text' })
  bodyText = '';

  // ============================================================================
  // Event handlers
  // ============================================================================

  private _onClose() {
    this.dispatchEvent(
      new CustomEvent<CsvErrorModalClosedEvent>('error-modal-closed', {
        bubbles: true,
        composed: true,
        detail: { element: this },
      }),
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  override render() {
    // NOTE: Using early-return pattern because inmar-hc-modal unmounts DOM on
    // .open=false. If the modal is replaced with one that keeps DOM mounted,
    // switch to .open=${this.open} pattern for better a11y (avoids focus jumps
    // on remount).
    if (!this.open) return nothing;

    return html`
      <inmar-hc-modal
        .open=${true}
        modal-title=${this.modalTitle}
        size="md"
        @modal-closed=${this._onClose}
      >
        <div slot="body">
          <inmar-hc-alert
            type="error"
            heading-text=${this.headingText}
            body-text=${this.bodyText}
          ></inmar-hc-alert>
        </div>
        <div slot="footer">
          <inmar-hc-button
            label="Close"
            variant="tertiary"
            @button-click=${this._onClose}
          ></inmar-hc-button>
        </div>
      </inmar-hc-modal>
    `;
  }
}
